import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapPin, Package, Loader2, ArrowLeft, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { LoadingState } from "@/components/States";
import { formatTRY, formatDate, timeAgo } from "@/lib/format";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function BuyRequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [br, setBr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [price, setPrice] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => api.get(`/buy-requests/${id}`).then(({ data }) => setBr(data.buy_request)).finally(() => setLoading(false));
  useEffect(() => { load(); }, [id]);

  const respond = async () => {
    if (!user) { navigate("/giris"); return; }
    setSending(true);
    try {
      await api.post(`/buy-requests/${id}/responses`, { message: msg, price: price ? parseFloat(price) : null });
      toast.success("Yanıtınız gönderildi");
      setMsg(""); setPrice(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  if (loading) return <LoadingState />;
  if (!br) return <div className="py-20 text-center text-slate-500">Alım talebi bulunamadı.</div>;

  const isOwner = user && user.user_id === br.buyer_id;

  return (
    <div className="mx-auto max-w-3xl px-6 py-8">
      <button onClick={() => navigate("/alim-talepleri")} className="mb-4 flex items-center gap-1 text-sm text-slate-500 hover:text-emerald-700">
        <ArrowLeft size={16} /> Alım Taleplerine Dön
      </button>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="flex items-start justify-between gap-3">
          <h1 className="font-display text-2xl font-extrabold text-slate-900">{br.product}</h1>
          {br.target_price != null && <span className="rounded-full bg-emerald-50 px-3 py-1 font-semibold text-emerald-700">~{formatTRY(br.target_price)}/{br.unit}</span>}
        </div>
        <div className="mt-3 flex flex-wrap gap-3 text-sm text-slate-500">
          {br.quantity != null && <span className="flex items-center gap-1"><Package size={14} />{br.quantity} {br.unit}</span>}
          {br.city && <span className="flex items-center gap-1"><MapPin size={14} />{br.city}</span>}
          <span className="flex items-center gap-1"><User size={14} />{br.buyer?.name}</span>
          <span className="ml-auto text-xs">{formatDate(br.created_at)}</span>
        </div>
        <p className="mt-4 whitespace-pre-line text-slate-700">{br.description}</p>
      </div>

      {!isOwner && (
        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="font-display text-lg font-bold text-slate-900">Teklif / Yanıt Gönder</h2>
          <div className="mt-3 space-y-3">
            <div className="space-y-1.5"><Label>Fiyat Teklifi (₺ / {br.unit})</Label><Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="brd-price" /></div>
            <div className="space-y-1.5"><Label>Mesaj</Label><Textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} data-testid="brd-message" placeholder="Elimde uygun ürün var, detaylar..." /></div>
            <Button onClick={respond} disabled={sending || !msg.trim()} data-testid="brd-send" className="bg-emerald-600 hover:bg-emerald-700">
              {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Yanıt Gönder
            </Button>
          </div>
        </div>
      )}

      <div className="mt-6">
        <h2 className="mb-3 font-display text-lg font-bold text-slate-900">Yanıtlar ({br.responses?.length || 0})</h2>
        {(!br.responses || br.responses.length === 0) ? (
          <p className="text-sm text-slate-500">Henüz yanıt yok.</p>
        ) : (
          <div className="space-y-3">
            {br.responses.map((r) => (
              <div key={r.response_id} className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-800">{r.seller?.name || "Satıcı"}</span>
                  {r.price != null && <span className="font-semibold text-emerald-700">{formatTRY(r.price)}/{br.unit}</span>}
                </div>
                <p className="mt-1.5 text-sm text-slate-600">{r.message}</p>
                <p className="mt-1 text-xs text-slate-400">{timeAgo(r.created_at)}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
